import * as React from 'react';
import { FormFieldGroup } from './RenderFormFieldsComponent';
import {
    CheckboxComponent,
    ColorPickerComponent,
    InputGroup,
    RadioButtonComponent,
    SelectComponent,
    TextAreaComponent,
} from '../../../utils/UtilsComponent';
import { getObjValueInDeep } from '../../../utils/DataStructuresUtils';
import { Control, Controller, RegisterOptions, UseFormUnregister, ValidationRule } from 'react-hook-form';
import { validateEmailPattern } from '../../../utils/utilsPattern';
import { useTranslate } from 'sbx-crm-menu-ui';
import SkillFileComponent from 'src/components/pages/ai-agent-v2/agentSetupComponent/agentSkillsComponent/SkillFileComponent';
import { FileProps } from '../DragAndDrop';
import { ColorPickerTransparencyComponent } from '../ColorPickerTransparencyComponent';
import { RangeInputComponent } from '../RangeInputComponent';

type Props = {
    item: FormFieldGroup;
    onSubmit?: () => void;
    control: Control<any> | undefined;
    baseObj?: {
        [key: string]: any;
    }; // obj to get default value from de object
    unregister?: UseFormUnregister<any>;
    setFiles?: (files: File[]) => void;
    files?: File[];
    filesComponent?: FileProps[];
    setFilesComponent?: (files: FileProps[]) => void;
    deleteFile?: (file: FileProps) => void;
};
const RenderFormFieldComponent = ({
    item,
    onSubmit,
    baseObj,
    control,
    unregister,
    setFiles,
    files,
    filesComponent,
    setFilesComponent,
    deleteFile,
}: Props) => {
    const { t: common } = useTranslate('common');
    const getField = ({
        item,
        onChange,
        value,
    }: {
        item: FormFieldGroup;
        onChange: (value: any) => void;
        value: any;
    }) => {
        switch (item.type) {
            case 'select':
                return (
                    <SelectComponent
                        disabled={item.disabled}
                        value={value || ''}
                        onChange={(value) => {
                            onChange(value as string);
                            if (item.onChange) {
                                item.onChange(value);
                            }
                            if (onSubmit) {
                                onSubmit();
                            }
                        }}
                        isMulti={item.isMulti}
                        placeholder={item.placeholder}
                        label={(item.label as string) ?? ''}
                        options={item.options}
                        classNameLabel={item.classNameLabel ?? ''}
                        required={(item?.rules?.required ?? false) as boolean}
                        classNameContent={item.classNameContent ?? ''}
                        classNameInput={item.classNameInput ?? ''}
                        creatable={false}
                    />
                );
            case 'radio':
                return (
                    <div className={`flex  ${item.classNameLabel ?? 'gap-3'}`}>
                        {item.label ?? ''}
                        <div className={`flex gap-2 flex-wrap ${item.classNameContent ?? 'items-center'} `}>
                            {item.options?.map((option, index) => {
                                return (
                                    <div
                                        className={`${item.classNameContentInput ?? ''}`}
                                        key={option.value + '_' + index}
                                    >
                                        <RadioButtonComponent
                                            label={(option.label as string) ?? ''}
                                            name={option.value}
                                            disabled={item.disabled}
                                            value={option.value}
                                            checked={option?.value?.toString() === value?.toString()}
                                            onChange={(value) => {
                                                onChange(value as string);
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            case 'color':
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_group_' + item.name + (item.id ?? '')}
                        >
                            <ColorPickerComponent
                                label={(item.label as string) ?? ''}
                                onChange={(value1) => {
                                    onChange(value1);
                                }}
                                name={item.name}
                                id={'id_input_group_' + item.name}
                                type={item.type ?? 'text'}
                                disabled={item.disabled}
                                value={value || undefined}
                                placeholder={item.placeholder ?? (item.label as string) ?? ''}
                                classNameContent={item.classNameContent}
                                classNameLabel={item.classNameLabel}
                                classNameInput={item.classNameInput}
                                required={(item?.rules?.required ?? false) as boolean}
                            />
                        </div>
                    </div>
                );
            case 'color_transparency':
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_group_' + item.name + (item.id ?? '')}
                        >
                            <ColorPickerTransparencyComponent
                                label={(item.label as string) ?? ''}
                                onChange={(value1) => {
                                    onChange(value1);
                                }}
                                name={item.name}
                                // id={'id_input_group_' + item.name}
                                // type={item.type ?? 'text'}
                                disabled={item.disabled}
                                oldValue={item.oldValue}
                                value={value || undefined}
                                placeholder={item.placeholder ?? (item.label as string) ?? ''}
                                classNameContent={item.classNameContent}
                                classNameLabel={item.classNameLabel}
                                classNameInput={item.classNameInput}
                                required={(item?.rules?.required ?? false) as boolean}
                            />
                        </div>
                    </div>
                );
            case 'checkbox':
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_checkbox_' + item.name + (item.id ?? '')}
                        >
                            <CheckboxComponent
                                label={(item.label as string) ?? ''}
                                onChange={(value1) => {
                                    onChange(value1);
                                }}
                                name={'name_input_checkbox_' + item.name}
                                id={'id_input_checkbox_' + item.name}
                                disabled={item.disabled}
                                checked={value}
                                placeholder={item.placeholder ?? (item.label as string) ?? ''}
                                classNameContent={item.classNameContent}
                                classNameLabel={item.classNameLabel}
                                classNameInput={item.classNameInput}
                                required={(item?.rules?.required ?? false) as boolean}
                            />
                        </div>
                    </div>
                );
            case 'textarea':
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_group_' + item.name + (item.id ?? '')}
                        >
                            <TextAreaComponent
                                label={(item.label as string) ?? ''}
                                min={item.min}
                                max={item.max}
                                maxLength={item.maxLength}
                                onChange={(value1) => {
                                    onChange(value1);
                                }}
                                name={'name_input_group_' + item.name}
                                id={'id_input_group_' + item.name}
                                type={item.type ?? 'text'}
                                disabled={item.disabled || item.disabled}
                                value={value || ''}
                                placeholder={item.placeholder ?? (item.label as string) ?? ''}
                                classNameContent={item.classNameContent}
                                classNameLabel={item.classNameLabel}
                                classNameInput={item.classNameInput}
                                required={(item?.rules?.required ?? false) as boolean}
                            />
                        </div>
                    </div>
                );
            case 'file':
                return (
                    <SkillFileComponent
                        setFilesComponent={setFilesComponent ?? (() => {})}
                        setFiles={setFiles ?? (() => {})}
                        files={files ?? []}
                        filesComponent={filesComponent ?? []}
                        deleteFile={(file) => (deleteFile ? deleteFile(file) : () => {})}
                        typeExcelOnly
                    />
                );
            case 'range':
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_group_' + item.name + (item.id ?? '')}
                        >
                            <RangeInputComponent
                                label={(item.label as string) ?? ''}
                                min={item.min as number}
                                max={item.max as number}
                                value={value}
                                oldValue={item.oldValue}
                                onChange={(value1) => {
                                    onChange(value1);
                                }}
                                disabled={item.disabled}
                                unit={item.unit}
                                color={item.colorRange}
                                classNameLabel={item.classNameLabel}
                            />
                        </div>
                    </div>
                );
            default:
                return (
                    <div className={`${item.classNameContentInput ?? ''}`}>
                        <div
                            className={`w-full ${item.classContainer ?? ''}`}
                            key={'key_input_group_' + item.name + (item.id ?? '')}
                        >
                            <InputGroup
                                label={(item.label as string) ?? ''}
                                min={item.min}
                                max={item.max}
                                maxLength={item.maxLength}
                                onChange={(value1) => {
                                    if (item.pattern) {
                                        if (item.pattern.test(value1)) {
                                            onChange(value1);
                                        }
                                    } else {
                                        if (item.type === 'number') {
                                            onChange(parseFloat(value1));
                                        } else {
                                            onChange(value1);
                                        }
                                    }
                                    if (item?.onChange) {
                                        item.onChange(value1);
                                    }
                                }}
                                name={'name_input_group_' + item.name}
                                id={'id_input_group_' + item.name}
                                type={item.type ?? 'text'}
                                disabled={item.disabled || item.disabled}
                                value={value}
                                checked={value}
                                placeholder={item.placeholder ?? (item.label as string) ?? ''}
                                classNameContent={item.classNameContent}
                                classNameLabel={item.classNameLabel}
                                classNameInput={item.classNameInput}
                                required={(item?.rules?.required ?? false) as boolean}
                            />
                        </div>

                        {item.buttonToggle ?? null}
                    </div>
                );
        }
    };

    const getFieldDefaultValue = (item: FormFieldGroup) => {
        if (
            item.noValidateDefaultValues &&
            ((typeof item.defaultValue === 'number' && item.defaultValue >= 0) ||
                typeof item.defaultValue === 'boolean' ||
                item.defaultValue ||
                item.defaultValue === '')
        ) {
            return item.defaultValue;
        }

        if (baseObj && item.defaultValue && typeof item.defaultValue === 'string') {
            const value = getObjValueInDeep(baseObj, item.defaultValue);
            if (value !== item.defaultValue) {
                return value;
            }
        }
        return null;
    };

    React.useEffect(() => {
        return () => {
            if (unregister) {
                unregister(item.name);
            }
        };
    }, []);

    return (
        <Controller
            defaultValue={getFieldDefaultValue(item)}
            name={item.name as any}
            render={({ field, fieldState, formState }) => {
                const errorMessage =
                    item.customErrorMessage ??
                    (fieldState.error?.message && fieldState.error.message.length > 0 ? (
                        fieldState.error?.message
                    ) : (
                        <span className={`${item.classMessageError ?? ''}`}>{common('this_field_is_required')}</span>
                    ));
                return (
                    <div className={`flex flex-col ${item?.classContainer ?? ''}`}>
                        {getField({
                            item,
                            onChange: field.onChange,
                            value: field.value,
                        })}
                        {item.sub_item && item.sub_item}
                        {(fieldState.error || getObjValueInDeep(formState.errors, item.name)?.type) && (
                            <span className="text-red-500 text-sm">
                                {item['messageByType']
                                    ? item.messageByType![fieldState?.error?.type as keyof RegisterOptions] ??
                                      errorMessage
                                    : errorMessage}
                            </span>
                        )}
                    </div>
                );
            }}
            control={control}
            rules={
                item.rules
                    ? {
                          ...item.rules,
                          pattern:
                              item.rules.pattern ??
                              ({
                                  value: item.type === 'email' ? validateEmailPattern : undefined,
                                  message: 'Please fill a valid email address.',
                              } as ValidationRule<RegExp>),
                      }
                    : undefined
            }
        />
    );
};

export default RenderFormFieldComponent;
